import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { ArrowUpRight, TrendingUp, TrendingDown } from "lucide-react";
import { convictionBadgeClass } from "@/lib/conviction";

export default function Conviction() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("active");

  useEffect(() => {
    client.get("/conviction").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const active = items.filter((c) => c.status !== "INVALIDATED");
  const wrong = items.filter((c) => c.status === "INVALIDATED");
  const shown = tab === "active" ? active : wrong;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12">
      <p className="font-mono-cl text-[11px] tracking-[0.28em] text-[#ccff00] uppercase mb-2">Track Record & Accountability</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">OnchainLyfe Conviction</h1>
      <p className="text-zinc-400 mt-3 max-w-2xl">
        Investment theses we believe are worth following, tracked from the day we publish them. Some will play out. Some won't. We keep both on record. For research purposes, not trading signals.
      </p>

      <div className="flex gap-2 mt-8 mb-8">
        <button
          data-testid="conviction-tab-active"
          onClick={() => setTab("active")}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "active" ? "bg-[#ccff00] text-black" : "border border-white/10 text-zinc-400 hover:text-white"}`}
        >
          Active Theses
        </button>
        <button
          data-testid="conviction-tab-wrong"
          onClick={() => setTab("wrong")}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "wrong" ? "bg-red-500 text-white" : "border border-white/10 text-zinc-400 hover:text-white"}`}
        >
          Invalidated
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {shown.map((c) => {
          const neg = (c.performance_value ?? 0) < 0;
          return (
            <Link key={c.slug} to={`/conviction/${c.slug}`} data-testid={`conviction-card-${c.slug}`} className="cl-card p-6 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <span className="font-display text-2xl font-bold">{c.asset}</span>
                <span className={`font-mono-cl text-[10px] tracking-[0.12em] uppercase px-2 py-1 rounded-full border ${convictionBadgeClass(c.status)}`}>
                  {c.status}
                </span>
              </div>
              <p className="text-sm text-zinc-300 font-medium mb-1">{c.title}</p>
              <p className="font-mono-cl text-[10px] tracking-[0.14em] uppercase text-zinc-600 mb-4">Horizon: {c.time_horizon}</p>
              <div className="mt-auto flex items-end justify-between pt-4 border-t border-white/5">
                <div>
                  <p className="font-mono-cl text-[10px] tracking-[0.14em] uppercase text-zinc-600 flex items-center gap-1.5">
                    {c.live && <span className="live-dot w-1.5 h-1.5 rounded-full bg-[#ccff00]" />}
                    {c.live ? "Live · since research" : "Since research"}
                  </p>
                  <p className={`font-mono-cl text-2xl font-bold flex items-center gap-1 ${neg ? "text-red-400" : "text-[#ccff00]"}`}>
                    {neg ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                    {c.performance || "-"}
                  </p>
                  {c.live && (
                    <p className="font-mono-cl text-[10px] text-zinc-500 mt-0.5">${c.entry_price} → ${c.live_price}</p>
                  )}
                </div>
                <ArrowUpRight className="w-5 h-5 text-zinc-600" />
              </div>
            </Link>
          );
        })}
        {shown.length === 0 && <p className="text-zinc-500 py-16">Nothing here yet.</p>}
      </div>
    </div>
  );
}
